// "Update Code" — tell devices to pull their firmware .bin from the server and
// flash themselves.
//
// ⚠️ NOT THE SAME THING AS THE APP OTA. Two different systems, both called OTA:
//   • App OTA  (core/ota/*)  replaces the React bundle, served from anuplights.com
//   • THIS      firmware OTA  makes an ESP device fetch its .bin from
//                             smartifyindia.co.in/uploads/bin/ and reflash
// Nothing here touches the app bundle, and nothing there touches a device.
//
import {
  getRooms, getCommonDevices, getDevicesByRoom, updateDeviceInstalled,
} from '../api/api';
import { publishCommand, publishRaw } from '../utils/deviceController';
import { getStatusCached, ONLINE, UNKNOWN } from './deviceStatus';
import { probeLiveness } from './liveness';
// Reporting-only, and one-way: firmwareInfo answers "is there any point sending
// it", which is the evidence `clearFinishedUpdateMarks` needs. It does not
// import back.
import { CURRENT } from './firmwareInfo';

// ────────────────────────────────────────────────────────────────────────────
// The command the ESP8266 firmware listens for.
//
// The firmware accepts FOUR spellings, and upper-cases the word before matching:
//
//   tempword == "UPDATECODE" || "UPDATE CODE" || "CODEUPDATE" || "CODE UPDATE"
//
// so case and the space are both irrelevant here — "update code" and
// "updatecode" are equally valid. `updatecode` is used because it matches the
// working MQTT console's preset list, and lowercase is the house style for every
// simple word command.
//
// See core/devices/commands.js for the one real exception: ADDWIFI / REMOVEWIFI
// are NOT upper-cased by the firmware, because an SSID and password are
// case-sensitive — those are exact literals.
export const FIRMWARE_UPDATE_COMMAND = 'updatecode';

// How the command goes on the wire.
//   false -> `[<uno>]update code#<userId>`  — the normal device-command envelope,
//                                             what every relay/fan/light command
//                                             uses. The firmware substring-matches
//                                             the payload, so the command is found
//                                             inside the envelope.
//   true  -> `update code`                  — raw, the way `getallwifi` goes out
//                                             (Flutter's devController drops the
//                                             envelope when given a buttonId).
//
// Set to the envelope form, since that is what every functional device command
// uses; only queries like getallwifi are sent raw. The confirmation dialog prints
// the exact payload before publishing, so flip this if the firmware wants it bare.
export const FIRMWARE_UPDATE_UNWRAPPED = false;
// ────────────────────────────────────────────────────────────────────────────

// Liveness comes from THE SAME CHECK THAT DRAWS THE DOT — deviceStatus's
// getStatusCached, which asks GetDeviceStatusbyUniqueNumber.
//
// ⚠️ AN EARLIER VERSION HAD ITS OWN CHECK AND IT WAS WRONG TWICE OVER, so that a
// device showing a green dot was reported offline and refused an update:
//
//  1. It read LastUpdatedDateTime off the DEVICE LIST row, which is not the same
//     heartbeat the status table records.
//  2. It stripped the trailing 'Z' before parsing — treating a UTC timestamp as
//     local time. In IST that reads 5½ hours in the past, so the 60-second window
//     could never be met and EVERY device looked offline.
//
// Beyond the parsing, the rule is: whatever the user sees on the tile is what the
// action must act on. Two independent liveness checks will drift, and the user
// only ever sees one of them. getStatusCached is cached for 45 s and shared with
// the tiles, so on a screen whose dots have already rendered this is free.
export const LIVE_WINDOW_MS = 60000;

const unoOf = (d) => d && (d.UniqueNumber ?? d.uniqueNumber);

// Status lookups in flight at once. A browser allows about six connections per
// host; fire fifty and the ones at the back sit in a queue until their own
// timeout fires, which reads as "offline" and gets worse the larger the scope.
export const STATUS_CONCURRENCY = 6;

// Promise.all with a ceiling. Results keep the order of `items`.
async function mapLimit(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      out[i] = await fn(items[i], i);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker()),
  );
  return out;
}

function dedupe(devices) {
  const seen = new Set();
  return devices.filter((d) => {
    const u = String(unoOf(d) ?? '');
    if (!u || seen.has(u)) return false;
    seen.add(u);
    return true;
  });
}

/** RoomID used for a device that belongs to the location, not to a room. */
export const COMMON_ROOM_ID = 0;

// The room a device was collected FROM, stamped onto the row.
//
// ⚠️ THE ROW DOES NOT RELIABLY CARRY IT. The per-user room endpoint returns
// RoomName, the common-devices one returns RoomId 0 and no name at all, and the
// unfiltered admin query (browseAll) returns neither. So the caller — which
// asked for a specific room and therefore knows the answer — fills it in. Group
// a list by RoomName without this and half of it lands under "undefined".
const stampRoom = (rows, roomId, roomName) => rows.map((d) => ({
  ...d,
  RoomID: d.RoomID ?? d.RoomId ?? roomId,
  RoomName: d.RoomName || roomName,
}));

// Every device in a room. The location is what makes "every" true — without it
// the room endpoint hides whole device types, and a device skipped here is a
// device left on old firmware while the dialog reports success.
//
// `roomName` is optional and only labels the rows: the per-user endpoint sends
// RoomName, but the unfiltered admin one (browseAll) does not, so the caller
// passes what it already knows rather than the list grouping under a blank.
export async function collectRoomDevices(userId, roomId, locationId, roomName) {
  const res = await getDevicesByRoom(userId, roomId, locationId).catch(() => null);
  const rows = Array.isArray(res?.data) ? res.data : [];
  return dedupe(stampRoom(rows, roomId, String(roomName || '').trim()));
}

// Every device in a location: each room's devices plus the location's common
// devices (which belong to no room and would otherwise be missed).
export async function collectLocationDevices(userId, locationId) {
  const [roomsRes, commonRes] = await Promise.all([
    getRooms(userId, locationId).catch(() => null),
    getCommonDevices(userId, locationId).catch(() => null),
  ]);
  const rooms = Array.isArray(roomsRes?.data) ? roomsRes.data : [];
  const perRoom = await Promise.all(
    rooms.map((r) => getDevicesByRoom(userId, r.RoomID, locationId)
      .then((res) => stampRoom(
        Array.isArray(res.data) ? res.data : [],
        r.RoomID,
        String(r.RoomName || '').trim(),
      ))
      .catch(() => [])),
  );
  const common = stampRoom(
    Array.isArray(commonRes?.data) ? commonRes.data : [],
    COMMON_ROOM_ID,
    '',
  );
  // Common devices FIRST: they are the location's own — gates, meters, the door
  // lock — and belong at the top of the sheet rather than after every room.
  return dedupe([...common, ...perRoom.flat()]);
}

// Split a device list into the ones that will be sent to and the ones skipped.
//
// ⚠️ THIS ASKS THE DEVICES, NOT THE SERVER. Two earlier versions read a
// timestamp and both got it wrong in ways that reported working hardware as
// offline, because a timestamp answers "when did a row last change" and the
// question here is "will this device receive what I am about to send".
//
// What went wrong with the timestamp approach, in order:
//   1. it read the device-list row, which is not the heartbeat at all
//   2. it stripped the trailing 'Z', parsing UTC as local
//   3. even reading the right table, those stamps are the server's LOCAL time
//      labelled UTC — five and a half hours out (see deviceStatus.js)
//   4. and one HTTP request per device, each racing a 6-second timeout, means a
//      location's worth of devices queues behind the browser's six-connection
//      limit until the ones at the back time out and report offline. The bigger
//      the scope, the more of it falsely reads as dead.
//
// An MQTT probe has none of that: one connection, no clock, and it exercises the
// exact path the command will take. Verified against live hardware.
//
// If the BROKER is unreachable, nothing answers — and reporting every device as
// offline would then be a fact about our connection, not the devices. In that
// case only, fall back to the cached status the tiles show, so the dialog and
// the badge still agree.
export async function splitByLiveness(devices) {
  const list = devices || [];
  if (!list.length) return { live: [], offline: [], method: 'mqtt' };

  const { live: answered, reachedBroker } = await probeLiveness(list.map(unoOf));

  if (!reachedBroker) {
    const marks = await mapLimit(list, STATUS_CONCURRENCY,
      (d) => getStatusCached(unoOf(d)).catch(() => UNKNOWN));
    const live = [];
    const offline = [];
    list.forEach((d, i) => (marks[i] === ONLINE ? live : offline).push(d));
    return { live, offline, method: 'status', viaStatus: live.length };
  }

  // Silence is strong evidence, but not proof: a device whose firmware predates
  // `getallwifi` would be alive and mute, and skipping it would be a NEW way to
  // report working hardware as offline — the exact fault being fixed here.
  //
  // So the ones that did not answer get a second chance against the status
  // table, where a row inside the last minute is a real sighting. Only those,
  // and only a few at a time: one request per device, all at once, is what made
  // whole locations read as dead in the first place.
  const quiet = list.filter((d) => !answered.has(String(unoOf(d))));
  const marks = await mapLimit(quiet, STATUS_CONCURRENCY,
    (d) => getStatusCached(unoOf(d)).catch(() => UNKNOWN));
  const recent = new Set();
  quiet.forEach((d, i) => { if (marks[i] === ONLINE) recent.add(String(unoOf(d))); });

  const live = [];
  const offline = [];
  list.forEach((d) => {
    const u = String(unoOf(d));
    (answered.has(u) || recent.has(u) ? live : offline).push(d);
  });
  return { live, offline, method: 'mqtt', viaStatus: recent.size };
}

// Gap between publishes. Longer than the scenes delay (350 ms) on purpose: each
// device that accepts this immediately starts an HTTP download of its .bin, so
// firing a whole location at once would have every device hit the file server
// simultaneously as well as flooding the broker.
export const UPDATE_STEP_DELAY_MS = 800;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Publish the update command to each device in turn.
//
// Only ever called with the LIVE list: an offline device would just drop the
// message, and it would look like it had been updated.
//
// onProgress(sentCount, total, device) fires after each publish so the UI can
// show progress — a location rollout is not instant.
export async function sendFirmwareUpdate(devices, userId, onProgress) {
  const list = devices || [];
  for (let i = 0; i < list.length; i += 1) {
    const uno = unoOf(list[i]);
    if (uno) {
      if (FIRMWARE_UPDATE_UNWRAPPED) publishRaw(uno, FIRMWARE_UPDATE_COMMAND);
      else publishCommand(uno, FIRMWARE_UPDATE_COMMAND, userId);
    }
    if (onProgress) onProgress(i + 1, list.length, list[i]);
    // eslint-disable-next-line no-await-in-loop
    if (i < list.length - 1) await sleep(UPDATE_STEP_DELAY_MS);
  }
  return list.length;
}

// ════════════════════════════════════════════════════════════════════════════
// THREE ROUTES TO THE SAME INSTRUCTION, and only one of them is instant
// ════════════════════════════════════════════════════════════════════════════
//
// MQTT alone reaches a device that is connected AT THIS SECOND, and reaches
// nothing else — a publish to a device that is not listening is dropped by the
// broker without an error at either end. On an estate where most devices are
// asleep most of the time (99 of 551 assigned devices had pinged in the last
// day when this was measured), that is a minority of the fleet.
//
// So an update sets all three, and they differ in WHEN they fire and in how
// long they survive. All three are read out of the firmware and the API, not
// assumed:
//
//   ┌─────────────────────┬───────────────────┬──────────────────────────────┐
//   │ route               │ fires             │ survives                     │
//   ├─────────────────────┼───────────────────┼──────────────────────────────┤
//   │ MQTT `updatecode`   │ now, if connected │ nothing — one shot           │
//   │ PendingCommand      │ next poll, ≤20min │ until READ (not until done)  │
//   │ RequireUpdateCode   │ next Wi-Fi connect│ until the flash SUCCEEDS     │
//   └─────────────────────┴───────────────────┴──────────────────────────────┘
//
// ── PendingCommand ─────────────────────────────────────────────────────────
// `OtaForSwitching.ino:1591` — `millis() > LastGetPendingCommandTime + 1200000`
// — polls `GetPendingCommandFromDevicesInstalledbyUniqueNumber` every 20
// minutes and runs whatever comes back through the SAME dispatcher that handles
// MQTT commands.
//
// ⚠️ THE SERVER EMPTIES THE SLOT AS SOON AS THE DEVICE READS IT, in the
// endpoint itself (controllers.js `GetPendingCommandFromDevicesInstalled…`
// writes `PendingCommand: ""` back after replying). So it clears on DELIVERY,
// not on success: a device that picks the command up and then fails to flash
// has an empty slot and an unchanged firmware. That is precisely why the mark
// below exists as well.
//
// ⚠️ IT IS A SINGLE SLOT, NOT A QUEUE, despite the name — writing again
// replaces whatever was pending. Queueing the same device twice is harmless;
// queueing a DIFFERENT command discards the first, which is why every automatic
// writer here checks `isUpdateCommand` first.
//
// ── RequireUpdateCode — the durable one ────────────────────────────────────
// A column on the same row, returned to the device by `VerifyDeviceNew`, which
// the firmware calls on every Wi-Fi connect (`OtaForSwitching.ino:2354` at boot
// and `:2461` on the fast-connect path). `OtaForSwitching.ino:2964`:
//
//     RequireUpdateCode = row["RequireUpdateCode"];
//     if (RequireUpdateCode == 1) { OTAUpdate(...); }
//
// ⚠️ AND THE DEVICE CLEARS IT ITSELF, in exactly two places — `:2750` on
// HTTP_UPDATE_OK, and `:2711` when the OTA link comes back empty. It is NOT
// cleared on HTTP_UPDATE_FAILED, so a device whose download fails keeps the
// mark and retries on its next connect. That is the behaviour that makes this
// the route worth having: it is the only one that survives its own failure.
//
// The cost of the same fact: a device that can NEVER take its image — a
// cross-chip file, a 404 — retries forever, every time its Wi-Fi reconnects.
// `clearFinishedUpdateMarks` is the app's side of that bargain.
export const PENDING_UPDATE_COMMAND = 'UPDATE CODE';

/** The install-row column the firmware reads on every Wi-Fi connect. */
export const UPDATE_MARK_COLUMN = 'RequireUpdateCode';

/**
 * Is whatever is parked in the slot already an update, however it was spelled?
 *
 * The firmware upper-cases and accepts four spellings (see
 * FIRMWARE_UPDATE_COMMAND), and this slot is written by hand as often as by the
 * app, so `updatecode`, `Update Code` and `CODE UPDATE` are all the same
 * instruction and none of them is worth refusing to overwrite.
 *
 * Used to decide whether an automatic queue is REPLACING something or merely
 * repeating it. Anything else — `RESTART`, `ALLOFF`, a typo — is somebody's
 * intent and is left alone.
 */
export const isUpdateCommand = (s) => {
  const w = String(s || '').replace(/\s+/g, '').toUpperCase();
  return w === 'UPDATECODE' || w === 'CODEUPDATE';
};

/**
 * Set BOTH server-side routes on one device, in a single row write.
 *
 * ⚠️ ONE WRITE, NOT TWO, and that is the point. Two requests are two chances to
 * half-apply: a device left with the mark but no parked command updates on its
 * next reboot instead of within twenty minutes, and one left with the parked
 * command but no mark gets a single attempt that vanishes on delivery whether
 * or not it worked. Neither failure says anything on screen.
 */
export const markFirmwareUpdate = (uniqueNumber) => updateDeviceInstalled(uniqueNumber, {
  PendingCommand: PENDING_UPDATE_COMMAND,
  [UPDATE_MARK_COLUMN]: 1,
});

/**
 * Take both back off.
 *
 * ⚠️ THE PARKED COMMAND IS ONLY CLEARED IF IT IS AN UPDATE. The slot is one
 * deep and is written by hand as often as by the app, so somebody's `RESTART`
 * must survive us tidying up after an update — see `isUpdateCommand`, which
 * accepts every spelling the firmware does. Pass the row's current
 * `PendingCommand` as `parked`; pass nothing and only the mark is cleared.
 */
export function clearFirmwareUpdateMark(uniqueNumber, parked) {
  const data = { [UPDATE_MARK_COLUMN]: 0 };
  if (isUpdateCommand(parked)) data.PendingCommand = '';
  return updateDeviceInstalled(uniqueNumber, data);
}

// Row writes in flight at once. Same ceiling as the status lookups and for the
// same reason — a browser allows about six connections per host, and a location
// rollout is forty of these.
export const MARK_CONCURRENCY = 6;

/**
 * Mark a list of devices. -> { marked, failed } counts.
 *
 * Deliberately NOT spaced out the way `sendFirmwareUpdate` is: these are REST
 * writes to a table, not a fleet of radios waking up at once. The staggering
 * that matters happens later, when the devices pick the command up on their own
 * poll timers, which are already scattered.
 *
 * ⚠️ A FAILED ROW IS COUNTED, NOT THROWN. One device whose write fails is one
 * device that will not update — it is not a reason to abandon the other forty,
 * and the caller reports the number so a partial run cannot read as a whole one.
 */
export async function markFirmwareUpdates(devices) {
  const list = (devices || []).filter((d) => unoOf(d));
  const ok = await mapLimit(list, MARK_CONCURRENCY,
    (d) => markFirmwareUpdate(unoOf(d)).then(() => true).catch(() => false));
  const marked = ok.filter(Boolean).length;
  return { marked, failed: list.length - marked };
}

/**
 * The whole instruction: mark every selected device, then publish to the ones
 * that are listening.
 *
 * -> { marked, failed, sent, total }
 *
 * ⚠️ THE ORDER IS NOT COSMETIC. The row writes go first because they are the
 * routes that survive: if the tab is closed, the network drops or the operator
 * walks away half way through the MQTT walk — which is spaced and can take a
 * minute over a location — every device is still instructed. Publishing first
 * would leave the unlucky half with nothing at all.
 *
 * `live` is the subset worth publishing to. Publishing to the rest is not
 * harmful, just pointless: it is UPDATE_STEP_DELAY_MS of wall-clock each for a
 * message the broker drops on the floor.
 */
export async function runFirmwareUpdate(devices, live, userId, onProgress) {
  const all = (devices || []).filter((d) => unoOf(d));
  const reachable = (live || []).filter((d) => unoOf(d));
  const report = (p) => { if (onProgress) onProgress(p); };

  report({ phase: 'marking', marked: 0, sent: 0, total: all.length });
  const { marked, failed } = await markFirmwareUpdates(all);
  report({ phase: 'marked', marked, failed, sent: 0, total: all.length });

  const sent = await sendFirmwareUpdate(reachable, userId, (n) => report({
    phase: 'sending', marked, failed, sent: n, total: reachable.length,
  }));
  report({ phase: 'done', marked, failed, sent, total: all.length });
  return { marked, failed, sent, total: all.length };
}

/**
 * Take the mark off devices that have finished — the other half of the bargain.
 *
 * ⚠️ IN THE NORMAL CASE THE DEVICE DOES THIS ITSELF (`OtaForSwitching.ino:2750`
 * posts RequireUpdateCode 0 on HTTP_UPDATE_OK), and the server empties the
 * parked slot the moment it is read. This is the backstop for the cases the
 * device cannot cover, and they are not rare:
 *
 *   • a mark set by hand, or by the WhatsApp `muc` path, on a device that was
 *     already on the current build — it flashes once for nothing and only then
 *     clears itself;
 *   • a device that took the update and was powered off before it could report;
 *   • an image it can never accept — wrong chip, missing file — which leaves the
 *     mark set and re-attempts on EVERY Wi-Fi reconnect, forever.
 *
 * The evidence used is `firmwareStateOf(row) === CURRENT`, i.e. the device's
 * recorded flash is at or after the build it would be sent to. Nothing else
 * would do: a mark is not evidence about firmware, and a heartbeat is not
 * either.
 *
 * ⚠️ IT WILL NOT CLEAR AN UNKNOWN. A row with no `CodeUpdated`, or a build with
 * no date, is left marked. Clearing on "don't know" would cancel a rollout that
 * has not happened, silently — the expensive direction. Leaving it costs one
 * redundant flash.
 *
 * Pure: is there a mark on this device that has already done its job?
 * Takes the FACTS from `firmwareStateOf`, so the rule lives in one place and is
 * testable without a server.
 */
export const updateMarkIsSpent = (f) => !!f
  && (f.updateMarked || isUpdateCommand(f.pendingCommand))
  && f.state === CURRENT;

/**
 * Takes the Map that `firmwareInfoFor` returns (unique number -> facts).
 *
 * -> the devices actually cleared, each as `{ uniqueNumber, updateMarked,
 *    pendingCommand }` describing WHAT THE ROW NOW HOLDS. Returning the new
 *    state rather than a bare count is what lets a caller correct its display
 *    without re-reading: a screen that says "marked" about a mark this function
 *    has just removed is telling the operator to act on something that is gone.
 *    Note `pendingCommand` survives when it was somebody else's command.
 */
export async function clearFinishedUpdateMarks(info) {
  const entries = (info instanceof Map ? [...info.entries()] : (info || []))
    .filter(([uno, f]) => uno && updateMarkIsSpent(f));
  const out = await mapLimit(entries, MARK_CONCURRENCY,
    ([uno, f]) => clearFirmwareUpdateMark(uno, f.pendingCommand)
      .then(() => ({
        uniqueNumber: uno,
        updateMarked: false,
        pendingCommand: isUpdateCommand(f.pendingCommand) ? '' : f.pendingCommand,
      }))
      .catch(() => null));
  return out.filter(Boolean);
}

/**
 * Park ANY command on the device for its next poll.
 *
 * The device runs whatever comes back through the same dispatcher that handles
 * MQTT, so this is not limited to updates — `RESTART`, `STATUS`, `ALLOFF` and
 * the rest all work, and reach a device that is not currently listening.
 *
 * ⚠️ IT IS ONE SLOT, NOT A QUEUE. Writing replaces whatever was pending; there
 * is no history and no confirmation that the device ever picked it up. The only
 * evidence is the device acting on it.
 *
 * ⚠️ A TYPO IS SILENT. An unrecognised command is not an error at either end —
 * the device simply does nothing with it, and the slot stays occupied until
 * something overwrites it. Same failure mode as a mistyped MQTT command.
 *
 * Pass '' to clear the slot.
 */
export const setPendingCommand = (uniqueNumber, command) =>
  updateDeviceInstalled(uniqueNumber, { PendingCommand: String(command ?? '') });

// Exactly what goes on the wire, for the confirmation dialog to display.
export function previewPayload(uniqueNumber, userId) {
  return FIRMWARE_UPDATE_UNWRAPPED
    ? FIRMWARE_UPDATE_COMMAND
    : `[${uniqueNumber || '<uno>'}]${FIRMWARE_UPDATE_COMMAND}#${userId}`;
}
